import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  StyleSheet,
  Platform,
  ScrollView,
  TextInput,
  TouchableOpacity,
  NativeSyntheticEvent,
  NativeScrollEvent,
  Image,
  Linking,
  Text,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { colors, fonts } from '../styles';
import { marksInitialScrollDone, nextScrollAction, type ScrollTrigger } from './liveChatScroll';
import { Button, Typography } from './ui';
import { useLiveChat, type LiveSpectator } from '../hooks/useLiveChat';
import { api } from '../services/api';
import { storage } from '../utils/storage';
import { mergeSpectators, resolveSpectatorName } from '../utils/guestEntry';
import { resolveLiveParticipantCount } from '../utils/liveParticipantCount';
import { useTheme } from '../context/ThemeContext';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { useI18n } from '../i18n';
import { linkifyText } from '../utils/linkifyText';

import { safeDisplayName } from '../utils/safeDisplayName';
import { chatAvatarSource } from '../utils/chatAvatarSource';
import { getFileUrl } from '../services/api';
import { COMMUNITY_LOGO, isCommunityHost } from '../utils/communityBrand';
import { ShareModal } from './ShareModal';
import type { ShareTarget } from '../services/shareAnalytics';
import { shareOrOpenModal } from '../utils/roomShare';
import { fontWeights } from '../styles/typographyTokens';
import { openAgapellaPaywall } from '../services/agapellaGate';
import { ACCENT_TEXT, ON_ACCENT_TEXT, accentTextOn, mutedText } from '../styles/accentText';
import { surfaceFill } from '../styles/platformBackgrounds';

const WEB = Platform.OS === 'web';

/**
 * CORE-1733 (возврат QA 27.10) — имя ведущего и его бейдж. #3B82F6 на поверхности
 * v4 давал 3.5:1, белый на нём — 3.68. На вебе — тёмно-синий в светлой теме
 * (≥5.9 на всех стопах поверхности) и светлый в тёмной; бейдж — #2563EB под
 * белым (5.17). Натив — прежний синий.
 */
const HOST_INK_WEB = { light: '#1D4ED8', dark: '#60A5FA' } as const;
const HOST_BADGE_FILL = WEB ? '#2563EB' : '#3B82F6';

/** CORE-1778 — хаб отказал по подписке Agapēlla. */
export const LIVE_CHAT_AGAPELLA_TEXT = 'Чат входит в подписку Agapēlla';

/**
 * Open a link from a chat message. Always in a NEW tab on web: the chat lives
 * beside a live session or a recording, and navigating the current tab would
 * tear that down (and lose a half-typed message).
 */
function openChatLink(url: string): void {
  if (Platform.OS === 'web') {
    if (typeof window !== 'undefined') {
      // noopener/noreferrer — the opened page gets no handle on ours.
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    return;
  }
  Linking.openURL(url).catch(() => {
    // Nothing actionable for the user — a malformed URL in someone's message.
  });
}

interface LiveChatPanelProps {
  liveId: number;
  isAuthenticated: boolean;
  authToken: string | null;
  onAuthRequired: () => void;
  /** CORE-1525 — код приглашения и превью-токен из ссылки: без них Live
   *  «по приглашению» не пускает в чат даже приглашённого. */
  inviteCode?: string | null;
  previewToken?: string | null;
  /** If true, renders in immersive full-height mode (Insight Timer style) */
  immersive?: boolean;
  /** Optional viewer count to display */
  viewerCount?: number;
  /** Master/host user name for HOST badge */
  hostName?: string;
  /** Callback when "About this Event" is tapped */
  onAboutEvent?: () => void;
  /** Callback when viewer count changes (from SignalR) */
  onViewerCountChange?: (count: number) => void;
  /** Callback when Invite is tapped */
  onInvite?: () => void;
  /** Event info for the "About this Event" modal */
  eventTitle?: string | null;
  eventDescription?: string | null;
  eventMasterName?: string | null;
  eventMasterPhotoUrl?: string | null;
  /** Чат закрыт на запись — история видна, поля ввода и донатов нет.
   *  CORE-1253: повод — отменённый эфир, а на вебе ещё и завершённый
   *  (уточнение ТЗ 20.10.2026): после конца чат остаётся историей, а поздний
   *  ответ мастера идёт в обсуждении анонса — см. `continueConversation`. */
  readOnly?: boolean;
  /** CORE-1253 — Live завершилась: под историей вместо «Чат закрыт» кнопка в
   *  обсуждение анонса (`discussion`) или, если его нет, в профиль
   *  организатора (`profile`). Действует только вместе с `readOnly`. */
  continueConversation?: { target: 'discussion' | 'profile'; onPress: () => void };
  /** Прячет шапку панели (индикатор связи + «Об этом мероприятии»). Нужен
   *  сайдбару страницы мастера после эфира: он показывает ту же информацию
   *  сам. Раньше шапку прятал `readOnly`, из-за чего «убрать дубль» и
   *  «запретить писать» нельзя было выбрать по отдельности. */
  hideHeader?: boolean;
  /** Чат смотрит сам ведущий. Прячет «Подарить»: раньше эта кнопка на
   *  странице мастера после эфира не показывалась просто потому, что там
   *  был `readOnly`, — а открыв чат на запись, мы бы предложили мастеру
   *  задонатить самому себе. CORE-1253. */
  isOwner?: boolean;
  /** Tapping a message author's avatar or name calls this with the author's
   *  userId — wired by the screen to open that user's public profile. AMB-247. */
  onUserPress?: (userId: string) => void;
  /** Total participant count shown on the toggle button. When provided, the
   *  toggle button appears next to the connection dot in the panel header. */
  participantCount?: number;
  /** Whether the parent is currently showing the participants list instead of chat. */
  showingParticipants?: boolean;
  /** Called when the toggle button is pressed. */
  onParticipantsPress?: () => void;
  /** Rendered in place of the chat content when showingParticipants is true. */
  participantsContent?: React.ReactNode;
}

const DONATION_AMOUNTS = [1.99, 4.99, 9.99, 19.99];

// Demo participant avatars removed — backend doesn't yet expose a viewer
// roster on the live, and showing fake faces ("the Pravatar 7") is worse
// than nothing. Once /api/lives/{id}/viewers ships we'll plug it in here.
const MOCK_PARTICIPANTS: Array<{ id: number; color: string; img: string }> = [];

export const LiveChatPanel: React.FC<LiveChatPanelProps> = ({
  liveId,
  isAuthenticated,
  authToken,
  onAuthRequired,
  inviteCode,
  previewToken,
  immersive = false,
  viewerCount,
  onViewerCountChange,
  hostName,
  onAboutEvent,
  onInvite,
  eventTitle,
  eventDescription,
  eventMasterName,
  eventMasterPhotoUrl,
  readOnly = false,
  continueConversation,
  hideHeader = false,
  isOwner = false,
  participantCount,
  showingParticipants = false,
  onParticipantsPress,
  participantsContent,
  onUserPress,
}) => {
  // CORE-614: live chat is served by the in-house main-API SignalR hub
  // (`/hubs/chat` via useLiveChat). Guests can read (anonymous JoinLiveChat);
  // sending requires auth. The former GetStream `livestream:live-{id}` path
  // and its anon connect-as-spectator were removed with the rest of the
  // server-side Stream integration.
  // CORE-528/846 — a no-account listener asks the server for a stable identity
  // and then announces it on the hub, so the room sees a named «Слушатель»
  // instead of an anonymous +1 nobody can address. Authenticated viewers keep
  // the old path: they already have a name and a presence row.
  const [spectator, setSpectator] = useState<LiveSpectator | null>(null);
  useEffect(() => {
    if (isAuthenticated || readOnly) { setSpectator(null); return; }
    let cancelled = false;
    Promise.all([api.spectateLive(liveId), storage.getGuestDisplayName().catch(() => null)])
      .then(([res, storedGuestName]) => {
        if (cancelled || !res?.spectator?.spectatorId) return;
        setSpectator({
          spectatorId: res.spectator.spectatorId,
          // The name the guest chose wins over the server's fallback, and it is
          // read from storage rather than props — that is what makes a refresh
          // survivable instead of renaming the guest in front of everyone.
          name: resolveSpectatorName(storedGuestName, res.spectator.name),
        });
      })
      .catch(() => { /* listener roster is a nicety; chat must still work */ });
    return () => { cancelled = true; };
  }, [liveId, isAuthenticated, readOnly]);

  const { messages, historyReady, viewerCount: chatViewerCount, spectators, spectatorCount, connectionState, error, subscriptionRequired, sendMessage, canSend, retry } = useLiveChat(liveId, authToken, spectator, { inviteCode, previewToken });

  // Two sources merge because neither alone is complete: our own row lands
  // before the server echo (and survives a reconnect that empties the list),
  // and whoever joined before us we never witnessed — they stay as a count.
  const listenerRows = useMemo(
    () => mergeSpectators(spectators, spectator, spectatorCount),
    [spectators, spectator, spectatorCount],
  );
  // CORE-846 — общий счётчик участников должен включать гостей-слушателей.
  // `participantCount` — это ИМЕНОВАННЫЙ ростер (cohorts + авторизованные
  // зрители), собранный родителем; анонимного гостя в нём нет. `spectatorCount`
  // с хаба — отдельное непересекающееся множество, поэтому складываем (не
  // через `viewerCount`, где гость уже посчитан через JoinLiveChat).
  const totalParticipantCount = resolveLiveParticipantCount({
    namedCount: participantCount,
    spectatorCount,
  });
  const { colors: themeColors, isDark } = useTheme();
  const { t, i18n } = useI18n('common');
  // Donate CTA gated behind a server feature flag. Dev defaults to ON,
  // prod stays OFF until the payments backend is ready. Russian copy
  // «Подарить» — matches the «дарение» framing in product.
  const featureFlags = useFeatureFlags();
  const donateEnabled = featureFlags.donate === true;
  const [inputText, setInputText] = useState('');
  const [showDonation, setShowDonation] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const scrollViewRef = useRef<ScrollView>(null);
  const isNearBottomRef = useRef(true);

  // Forward viewer count from SignalR to parent
  useEffect(() => {
    if (onViewerCountChange && chatViewerCount > 0) {
      onViewerCountChange(chatViewerCount);
    }
  }, [chatViewerCount, onViewerCountChange]);

  // Use SignalR viewer count if available, fall back to prop
  const effectiveViewerCount = chatViewerCount > 0 ? chatViewerCount : viewerCount;

  // CORE-1439 — прокрутка привязана к раскладке, а не к таймеру.
  //
  // Здесь стоял `setTimeout(…, 50)` после изменения числа сообщений. К этому
  // моменту список ещё не разложен — аватары не загружены, длинная история не
  // отрисована, — и `scrollToEnd` уезжал в конец той высоты, что была тогда.
  // Она короткая, поэтому человек оставался в начале истории и доскраливал
  // руками. Теперь решение принимается на `onContentSizeChange`: размер
  // изменился — значит высота уже настоящая.
  const didInitialScrollRef = useRef(false);

  const pinToBottom = useCallback((trigger: ScrollTrigger) => {
    const action = nextScrollAction({
      hasMessages: messages.length > 0,
      didInitialScroll: didInitialScrollRef.current,
      isNearBottom: isNearBottomRef.current,
      trigger,
    });
    if (action === 'none') return;

    scrollViewRef.current?.scrollToEnd({ animated: action === 'animate' });
    if (marksInitialScrollDone(action)) didInitialScrollRef.current = true;
  }, [messages.length]);

  // CORE-1439 (возврат QA 21.09) — размеры самой панели меняются отдельно от
  // содержимого, и их изменение тоже обязано ставить список на низ.
  //
  // Панель выезжает поверх видео: первый замер случается, пока её высота ещё
  // нулевая, и `scrollToEnd` туда «удаётся». Содержимое после этого не
  // меняется — меняется только высота, — поэтому повторить было нечем, и при
  // каждом повторном открытии чат оставался наверху.
  const handleLayout = useCallback(() => { pinToBottom('layout'); }, [pinToBottom]);
  const handleContentSizeChange = useCallback(() => { pinToBottom('content'); }, [pinToBottom]);

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const distanceFromBottom = contentSize.height - layoutMeasurement.height - contentOffset.y;
    isNearBottomRef.current = distanceFromBottom < 60;
  };

  const handleSend = async () => {
    if (!inputText.trim()) return;
    // CORE-529 — a guest never gets this far any more: the composer below is
    // replaced by the sign-in rail. Kept as a backstop in case a surface
    // renders the input for an anonymous viewer some other way.
    if (!isAuthenticated) {
      onAuthRequired();
      return;
    }
    const text = inputText;
    setInputText('');
    // CORE-1612 — не ушло (слишком часто, нет связи): возвращаем написанное,
    // если человек не начал печатать новое.
    const sent = await sendMessage(text);
    if (!sent) setInputText(current => (current.trim() ? current : text));
  };

  const handleKeyPress = (e: any) => {
    if (e.nativeEvent?.key === 'Enter' && !e.nativeEvent?.shiftKey) {
      e.preventDefault?.();
      handleSend();
    }
  };

  const handleDonate = (amount: number) => {
    // Donations are open to everyone — no auth gate
    setShowDonation(false);
    if (Platform.OS === 'web') {
      window.alert(t('chat.thankYou', { amount: amount.toFixed(2) }));
    }
  };

  // CORE-280 — unified share: a phone gets the OS sheet, a desktop the branded
  // ShareModal (CORE-786). Values computed at component scope so the modal
  // and the sheet share one source of truth.
  // `typeof window !== 'undefined'` is TRUE on React Native (window is a global
  // there), but `window.location` is undefined → reading `.origin` crashed the
  // native live room. Gate on the platform, not just window's presence.
  const shareBaseUrl = Platform.OS === 'web' && typeof window !== 'undefined'
    ? window.location.origin
    : (process.env.EXPO_PUBLIC_WEB_URL || 'https://agape.live');
  const shareUrl = `${shareBaseUrl}/live/${liveId}`;
  const shareTitle = eventTitle || 'Live Session';
  const shareMessage = `${shareTitle}\n${shareUrl}`;
  const shareTarget = useMemo<ShareTarget>(
    () => ({ entityType: 'live', entityId: liveId, surface: 'live_room' }),
    [liveId],
  );

  const handleShare = useCallback(() => {
    // CORE-786 — a phone gets the OS sheet, a desktop the popup.
    void shareOrOpenModal(
      { url: shareUrl, title: shareTitle, message: shareMessage },
      shareTarget,
      () => setShowShareModal(true),
    );
  }, [shareTitle, shareUrl, shareMessage, shareTarget]);

  const connectionDotColor =
    connectionState === 'connected' ? '#22C55E' :
      connectionState === 'reconnecting' ? '#E98245' :
        '#9CA3AF';

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMins = Math.floor((now.getTime() - d.getTime()) / 60000);
    if (diffMins < 1) return t('chat.time.now');
    if (diffMins < 60) return t('chat.time.minutesAgo', { count: diffMins });

    const locale = i18n.language;
    const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

    // A bare time reads as "today" — which is wrong the moment you open an
    // archived chat from a session that happened weeks ago. Show the date as
    // soon as the message isn't from today, and add the year once it's from
    // another one.
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) return time;

    const date = d.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
    });
    return `${date}, ${time}`;
  };

  // Generate avatar color from username
  const getAvatarColor = (name: string) => {
    const hues = [25, 35, 15, 200, 160, 280, 340, 45];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    const hue = hues[Math.abs(hash) % hues.length];
    return `hsl(${hue}, 45%, 55%)`;
  };

  const isHost = (userName: string) => {
    if (!hostName) return false;
    return userName.toLowerCase() === hostName.toLowerCase() ||
      userName.split('@')[0].toLowerCase() === hostName.toLowerCase();
  };

  const containerStyle = immersive ? s.containerImmersive : s.container;
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  return (
    // CORE-1733 — в эфире (immersive) на вебе чат прозрачен: фон даёт колонка
    // LiveDetail — локальная поверхность v4, а не своя плита #FAFAF8 / #141416.
    // Вне эфира (анонс, завершённая Live) колонка чата сама — поверхность v4:
    // возврат QA 27.10 нашёл её плоской #FFFFFF / #1A1A1A на /live после конца.
    // CORE-1253 — поверхность называет себя вслух. QA приняла этот чат за
    // тред обсуждения события: снаружи они различались только текстом
    // плейсхолдера. Теперь ответ на «какой чат открылся» даёт разметка.
    <View testID="live-chat-panel" style={[containerStyle, isDark && { backgroundColor: immersive ? '#141416' : '#1A1A1A' }, !isDark && immersive && { backgroundColor: '#FAFAF8' }, immersive && WEB && { backgroundColor: 'transparent' }, !immersive && WEB && surfaceFill(isDark ? 'dark' : 'light')]}>
      {/* Header — прячется по просьбе родителя (сайдбар после эфира уже
          показал ту же информацию выше), а не по режиму только-чтение. */}
      {!hideHeader && (
      <View style={[s.header, immersive && s.headerImmersive, isDark && { borderBottomColor: 'rgba(255,255,255,0.06)' }]}>
        <View style={s.headerLeft}>
          <View style={[s.connectionDot, { backgroundColor: connectionDotColor }]} />
          {onParticipantsPress !== undefined && (
            <TouchableOpacity onPress={onParticipantsPress} style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginLeft: 8 }}>
              <Ionicons
                name={showingParticipants ? 'chatbubble-outline' : 'eye-outline'}
                size={16}
                color={showingParticipants ? accentTextOn('#E85D04') : (WEB ? themeColors.textMuted : (isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)'))}
              />
              {!showingParticipants && participantCount !== undefined && totalParticipantCount > 0 && (
                <Typography variant="caption" style={{ fontSize: 12, fontWeight: fontWeights.bold, color: WEB ? themeColors.textMuted : (isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)') } as any}>
                  {totalParticipantCount}
                </Typography>
              )}
            </TouchableOpacity>
          )}
        </View>
        <TouchableOpacity onPress={() => setShowAboutModal(!showAboutModal)}>
          {/* CORE-1733 (возврат QA 27.10) — ссылка-подпись: #3B82F6 на поверхности давал 3.68:1. */}
          <Typography variant="caption" style={[s.aboutEventLink, isDark && { color: '#60A5FA' }, WEB && { color: ACCENT_TEXT }]}>
            {showAboutModal ? t('buttons.hide') : t('chat.aboutEvent')}
          </Typography>
        </TouchableOpacity>
      </View>
      )}

      {/* Inline "About this Event" panel — appears inside sidebar, Insight Timer style.
          The header «Скрыть» link toggles the panel; we don't render a second
          close button inside, and the panel hugs its content (no fixed height)
          so a short description doesn't leave a tall white gap above the chat. */}
      {showAboutModal && (
        // На вебе панель внутри поверхности чата прозрачна — непрозрачные поверхности не вкладываются.
        <View style={[s.aboutPanel, isDark && { backgroundColor: '#1E1E1E', borderBottomColor: 'rgba(255,255,255,0.06)' }, WEB && { backgroundColor: 'transparent' }]}>
          {/* Master avatar + title */}
          {eventMasterPhotoUrl ? (
            <Image source={{ uri: eventMasterPhotoUrl }} style={s.aboutMasterAvatar} />
          ) : eventMasterName ? (
            <View style={[s.aboutMasterAvatar, { backgroundColor: colors.solar, alignItems: 'center', justifyContent: 'center' }]}>
              <Typography variant="caption" style={{ color: ON_ACCENT_TEXT, fontWeight: fontWeights.bold, fontSize: 18 }}>
                {eventMasterName.charAt(0)}
              </Typography>
            </View>
          ) : null}

          <Typography variant="h5" style={[s.aboutTitle, isDark && { color: '#E8E4E0' }]}>
            {eventTitle || t('chat.liveSession')}
          </Typography>

          {eventMasterName && (
            <Typography variant="caption" style={[s.aboutMasterInfo, isDark && { color: 'rgba(255,255,255,0.45)' }, WEB && { color: themeColors.textMuted }]}>
              {t('chat.byMaster', { name: eventMasterName })}
            </Typography>
          )}

          {/* Description */}
          <Typography variant="bodySmall" style={[s.aboutDescription, isDark && { color: 'rgba(255,255,255,0.55)' }, WEB && { color: themeColors.textSecondary }]}>
            {eventDescription || t('chat.defaultDescription', { name: eventMasterName || 'us' })}
          </Typography>
        </View>
      )}

      {showingParticipants && participantsContent}

      {/* Connection status */}
      {!showingParticipants && connectionState === 'reconnecting' && (
        <View style={s.statusBanner}>
          <Ionicons name="sync-outline" size={14} color={accentTextOn('#E98245')} />
          <Typography variant="caption" style={[s.statusText, mutedText(0.5)]}>{t('chat.reconnecting')}</Typography>
        </View>
      )}
      {!showingParticipants && connectionState === 'disconnected' && !subscriptionRequired && (
        <View style={s.statusBanner}>
          <Ionicons name="cloud-offline-outline" size={14} color={WEB ? themeColors.textMuted : '#9CA3AF'} />
          <Typography variant="caption" style={[s.statusText, mutedText(0.5)]}>{t('chat.disconnected')}</Typography>
          <TouchableOpacity onPress={retry} style={s.retryBtn}>
            <Typography variant="caption" style={s.retryText}>{t('buttons.retry')}</Typography>
          </TouchableOpacity>
        </View>
      )}

      {/* Messages — Insight Timer style with avatars + HOST badge */}
      {!showingParticipants && <ScrollView
        ref={scrollViewRef}
        style={[s.messageList, immersive ? s.messageListImmersive : s.messageListCapped]}
        contentContainerStyle={s.messageListContent}
        showsVerticalScrollIndicator={false}
        onScroll={handleScroll}
        onContentSizeChange={handleContentSizeChange}
        onLayout={handleLayout}
        scrollEventThrottle={100}
      >
        {/* CORE-1253 — «будьте первым» только когда история уже пришла пустой. */}
        {messages.length === 0 && connectionState === 'connected' && historyReady && (
          <View style={s.emptyState}>
            <View style={s.emptyIconWrap}>
              <Ionicons name="chatbubbles-outline" size={28} color={WEB ? ACCENT_TEXT : 'rgba(237, 116, 61, 0.2)'} />
            </View>
            {/* CORE-1733 (возврат QA 27.10) — приглушение цветом темы, а не прозрачностью:
                .3 и .15 давали 1.85 / 1.33:1 (светлая) и 2.49 / 1.52 (тёмная). */}
            <Typography variant="caption" style={[s.emptyText, WEB ? { color: themeColors.textSecondary } : { opacity: 0.3 }]}>
              {t('chat.firstMessage')}
            </Typography>
            <Typography variant="caption" style={[s.emptyHint, mutedText(0.15)]}>
              {t('chat.sayHello')}
            </Typography>
          </View>
        )}
        {messages.map((msg, index) => {
          // BE returns the literal "User" string when AppUser.DisplayName is
          // null/empty (PII fallback gate). Substitute a deterministic "Гость
          // XXXX" so a chat full of email-registered users doesn't read as a
          // wall of identical "User" rows.
          const safeName = safeDisplayName(msg.userName, msg.userId);
          // Stream mode sets msg.isHost from the channel moderator roster;
          // legacy SignalR leaves it undefined, so fall back to name match.
          const host = msg.isHost ?? isHost(safeName);
          // AMB-248 — render the author's uploaded avatar when present
          // (resolved from the relative S3 key the BE sends as userImage),
          // else a coloured initial. Community-hosted events override with the
          // brand logo so the row doesn't read «С» (initial of «Сообщество»).
          const isCommunityAuthor = host && isCommunityHost(msg.userId, safeName);
          const avatar = chatAvatarSource(getFileUrl(msg.userImage), safeName);
          // AMB-247 — tap avatar/name to open the author's profile.
          const handleUserPress = onUserPress ? () => onUserPress(msg.userId) : undefined;
          return (
            <View key={msg.id} testID="live-chat-message" style={[s.messageRow, index === messages.length - 1 && { marginBottom: 8 }]}>
              {/* Avatar */}
              <View style={s.avatarContainer}>
                <TouchableOpacity
                  testID="live-chat-avatar"
                  onPress={handleUserPress}
                  disabled={!handleUserPress}
                  activeOpacity={0.7}
                  accessibilityRole={handleUserPress ? 'button' : undefined}
                  accessibilityLabel={handleUserPress ? `${safeName.split('@')[0]} — открыть профиль` : undefined}
                  style={Platform.OS === 'web' && handleUserPress ? ({ cursor: 'pointer' } as any) : undefined}
                >
                  {isCommunityAuthor ? (
                    <Image source={COMMUNITY_LOGO} style={s.avatar} resizeMode="cover" />
                  ) : avatar.kind === 'image' ? (
                    <Image source={{ uri: avatar.uri }} style={s.avatar} resizeMode="cover" />
                  ) : (
                    <View style={[s.avatar, { backgroundColor: getAvatarColor(safeName) }]}>
                      <Typography variant="caption" style={s.avatarText}>
                        {avatar.letter}
                      </Typography>
                    </View>
                  )}
                </TouchableOpacity>
                {host && (
                  <View style={s.hostBadge}>
                    <Typography variant="caption" style={s.hostBadgeText}>{t('chat.host')}</Typography>
                  </View>
                )}
              </View>
              {/* Message content — Insight Timer style (no bubble, just inline) */}
              <View style={s.messageContent}>
                <View style={s.messageHeader}>
                  <TouchableOpacity
                    testID="live-chat-author"
                    onPress={handleUserPress}
                    disabled={!handleUserPress}
                    activeOpacity={0.7}
                    accessibilityRole={handleUserPress ? 'button' : undefined}
                    style={Platform.OS === 'web' && handleUserPress ? ({ cursor: 'pointer' } as any) : undefined}
                  >
                    <Typography variant="caption" style={[s.authorName, host && s.authorNameHost, host && WEB && { color: HOST_INK_WEB[isDark ? 'dark' : 'light'] }, isDark && !host && { color: '#E8E4E0' }]}>
                      {safeName.split('@')[0]}
                    </Typography>
                  </TouchableOpacity>
                  <Typography variant="caption" style={[s.messageTime, mutedText(0.3)]}>
                    · {formatTime(msg.createdAt)}
                  </Typography>
                </View>
                <Typography variant="bodySmall" style={[s.messageText, { color: themeColors.textPrimary }]}>
                  {linkifyText(msg.text).map((seg, i) => (
                    seg.type === 'text' ? seg.value : (
                      // Plain <Text> rather than <Typography>: a nested Text is
                      // what makes an inline, pressable link inside a paragraph,
                      // and Typography takes no onPress.
                      <Text
                        key={`${seg.value}-${i}`}
                        onPress={() => openChatLink(seg.value)}
                        accessibilityRole="link"
                        style={[
                          s.messageLink,
                          Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : undefined,
                        ]}
                      >
                        {seg.label}
                      </Text>
                    )
                  ))}
                </Typography>
              </View>
            </View>
          );
        })}
      </ScrollView>}

      {/* Error banner */}
      {!showingParticipants && error && (
        <View style={s.errorBanner}>
          <Typography variant="caption" style={s.errorText}>{error}</Typography>
        </View>
      )}

      {/* Donation Panel (Insight Timer style) — same flag-gate as the
          bottom-bar trigger so a stale `showDonation=true` doesn't keep
          the panel rendered after the flag flips off. */}
      {donateEnabled && showDonation && (
        <View testID="live-chat-donation-panel" style={[s.donationPanel, isDark && { borderTopColor: 'rgba(255,255,255,0.06)', backgroundColor: 'rgba(237, 116, 61, 0.06)' }]}>
          <View style={s.donationHeader}>
            <Typography variant="overline" style={s.donationTitle}>{t('chat.offering')}</Typography>
            <TouchableOpacity testID="live-chat-donation-close" onPress={() => setShowDonation(false)} accessibilityRole="button">
              <Ionicons name="close" size={20} color={colors.gray} />
            </TouchableOpacity>
          </View>
          <View style={s.donationGrid}>
            {DONATION_AMOUNTS.map((amount) => (
              <TouchableOpacity
                key={amount}
                testID={`live-chat-donation-amount-${amount}`}
                style={[s.donationBtn, isDark && { backgroundColor: '#242424', borderColor: 'rgba(237, 116, 61, 0.35)' }]}
                onPress={() => handleDonate(amount)}
              >
                <Typography variant="button" style={s.donationBtnText}>
                  ${amount.toFixed(2)}
                </Typography>
              </TouchableOpacity>
            ))}
          </View>
          <TouchableOpacity
            testID="live-chat-donation-custom"
            style={[s.donationCustomBtn, isDark && { backgroundColor: 'rgba(255,255,255,0.06)' }]}
            onPress={() => handleDonate(0)}
          >
            <Typography variant="button" style={[s.donationCustomText, isDark && { color: themeColors.textSecondary }, WEB && !isDark && { color: themeColors.textMuted }]}>{t('chat.customAmount')}</Typography>
          </TouchableOpacity>
        </View>
      )}

      {/* ================================================
          INPUT AREA — прячется у закрытого чата (readOnly) и пока открыт
          список участников. CORE-1253: у завершённой Live закрытый чат — это
          история, а разговор продолжается в обсуждении анонса (кнопка ниже).
          ================================================ */}
      {!showingParticipants && readOnly ? (
        <View style={[s.inputArea, isDark && { borderTopColor: 'rgba(255,255,255,0.06)' }, { paddingVertical: 14 }]}>
          <Typography
            variant="caption"
            // CORE-1733 (возврат QA 27.10) — rgba(15,20,25,.5) на поверхности давал 3.49:1.
            // На вебе у поверхности есть контур — строке нужен отступ от него.
            style={{ textAlign: 'center', color: WEB ? themeColors.textMuted : (isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,20,25,0.5)'), fontSize: 13, ...(WEB ? { paddingHorizontal: 16 } : null) } as any}
          >
            {/* The `|| 'Эфир завершён…'` fallback that used to sit here never
                fired — i18next returns the KEY for a missing string, which is
                truthy — so the archive rail read a literal «chat.archiveNotice»
                until the key was added to ru/en/es. */}
            {continueConversation ? t('chat.endedNotice') : t('chat.archiveNotice')}
          </Typography>
          {continueConversation && (
            <Button
              testID="live-chat-continue"
              variant="solar"
              size="sm"
              leftIcon={continueConversation.target === 'discussion' ? 'chatbubbles-outline' : 'person-circle-outline'}
              onPress={continueConversation.onPress}
              accessibilityRole="button"
              style={s.continueButton}
            >
              {continueConversation.target === 'profile'
                ? t('chat.openMasterProfile')
                : isOwner ? t('chat.replyInDiscussion') : t('chat.continueInDiscussion')}
            </Button>
          )}
        </View>
      ) : (
      <View style={[s.inputArea, isDark && { borderTopColor: 'rgba(255,255,255,0.06)' }]}>
        {/* CORE-529 — a guest used to get a live, editable composer and was
            thrown to the login page only on send: the message was lost, and so
            was the stream they were watching. State the gate before they type.
            Reading the chat stays open to everyone (anonymous JoinLiveChat);
            only writing needs an account, and the rail navigates only when it
            is deliberately tapped. Same affordance the practice room shows its
            spectators, down to the string. */}
        {subscriptionRequired ? (
          // CORE-1778 — не исчезает и не переподключается; окно скажет, как открыть.
          <TouchableOpacity
            testID="live-chat-agapella"
            style={s.signInBar}
            onPress={() => openAgapellaPaywall({ kind: 'live', surface: 'live_chat', onUnlocked: retry })}
            activeOpacity={0.7}
            accessibilityRole="button"
          >
            <Ionicons name="lock-closed-outline" size={14} color={accentTextOn(colors.solar)} />
            <Typography variant="caption" style={s.signInText}>
              {LIVE_CHAT_AGAPELLA_TEXT}
            </Typography>
          </TouchableOpacity>
        ) : (
        <>
        {!isAuthenticated ? (
          <TouchableOpacity
            testID="live-chat-signin"
            style={s.signInBar}
            onPress={onAuthRequired}
            activeOpacity={0.7}
            accessibilityRole="button"
          >
            <Ionicons name="lock-closed-outline" size={14} color={accentTextOn(colors.solar)} />
            <Typography variant="caption" style={s.signInText}>
              {t('room.spectator.signInToChat')}
            </Typography>
          </TouchableOpacity>
        ) : (
        /* Input row: text field */
        <View style={s.inputBar}>
          <TextInput
            testID="live-chat-input"
            style={[s.textInput, { color: themeColors.textPrimary, backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15, 20, 25, 0.04)' }]}
            value={inputText}
            onChangeText={setInputText}
            onKeyPress={handleKeyPress}
            placeholder={t('chat.placeholder')}
            placeholderTextColor={WEB ? themeColors.textMuted : (isDark ? 'rgba(255,255,255,0.3)' : 'rgba(15, 20, 25, 0.3)')}
            maxLength={500}
            editable={connectionState === 'connected'}
          />
          <TouchableOpacity
            testID="live-chat-send"
            style={[s.sendBtn, (!inputText.trim() || !canSend) && s.sendBtnDisabled]}
            onPress={handleSend}
            disabled={!inputText.trim() || !canSend}
            accessibilityRole="button"
          >
             <View style={[s.sendIconCircle, isDark && { backgroundColor: 'rgba(255,255,255,0.1)' }]}>
                <Ionicons name="arrow-up" size={18} color={isDark ? "white" : "white"} />
             </View>
          </TouchableOpacity>
        </View>
        )}
        </>
        )}

        {/* CORE-528 AC 3 — no-account listeners, by name. The comment above
            MOCK_PARTICIPANTS said this spot was waiting for a server roster;
            CORE-846 shipped it, so the guest is a person here now instead of an
            anonymous +1. Rows dedupe on the stable spectatorId (AC 8, 9), and
            whoever was already watching when we arrived stays as a count rather
            than as invented rows. */}
        {listenerRows.rows.length > 0 && (
          <View style={{ paddingHorizontal: 16, paddingTop: 8, gap: 6 }}>
            {listenerRows.rows.map(row => (
              <View key={row.spectatorId} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Ionicons name="headset-outline" size={14} color={accentTextOn('#E98245')} />
                <Typography variant="caption" style={{ flex: 1, color: isDark ? '#FFF' : '#2B2730', fontWeight: row.isSelf ? '700' : '500' } as any}>
                  {row.isSelf ? `${row.name} (вы)` : row.name}
                </Typography>
                {/* CORE-528 QA-возврат 09.08 — та же подпись роли, что и в ростере
                    комнаты, из одного ключа. Литерал «Слушатель» здесь означал, что
                    на en/es поверхность отдавала русское слово, а две поверхности
                    расходились бы при любой правке формулировки. */}
                <Typography variant="caption" style={(WEB ? { color: themeColors.textMuted } : { opacity: 0.6, color: isDark ? '#FFF' : '#2B2730' }) as any}>
                  {t('room.spectator.roleLabel')}
                </Typography>
              </View>
            ))}
            {listenerRows.unnamedCount > 0 && (
              <Typography variant="caption" style={(WEB ? { color: themeColors.textMuted } : { opacity: 0.6, color: isDark ? '#FFF' : '#2B2730' }) as any}>
                {t('room.spectator.viewersWithoutAccount', { count: listenerRows.unnamedCount })}
              </Typography>
            )}
          </View>
        )}

        {/* Bottom bar: participant avatars left + Donate button right (Insight Timer style) */}
        <View style={s.bottomBar}>
          {/* Participant avatar stack */}
          <View style={s.participantStack}>
            {MOCK_PARTICIPANTS.slice(0, 6).map((p, i) => (
              <Image
                key={p.id}
                source={{ uri: p.img }}
                style={[
                  s.participantAvatar,
                  { backgroundColor: p.color, marginLeft: i === 0 ? 0 : -14, borderColor: isDark ? '#1A1A1A' : '#FAFAF8' },
                  { zIndex: MOCK_PARTICIPANTS.length - i },
                ]}
              />
            ))}
            {effectiveViewerCount !== undefined && effectiveViewerCount > 6 && (
              <View style={[s.participantAvatar, s.participantMore, { marginLeft: -14, zIndex: 0 }]}>
                <Typography variant="caption" style={[s.participantMoreText, WEB && { color: themeColors.textMuted }]}>
                  +{effectiveViewerCount - 6}
                </Typography>
              </View>
            )}
          </View>

          {/* Donate button — outlined style like Insight Timer.
              Hidden when the `donate` feature flag is off (default OFF on
              prod). Label is Russian «Подарить» — matches the framing
              already used in product copy. */}
          {donateEnabled && !isOwner && (
            <TouchableOpacity
              testID="live-chat-donate"
              style={[s.donateBtn, showDonation && s.donateBtnActive]}
              onPress={() => setShowDonation(!showDonation)}
              accessibilityRole="button"
              accessibilityLabel="Подарить"
            >
              <Typography variant="button" style={s.donateBtnText}>Подарить</Typography>
            </TouchableOpacity>
          )}
        </View>
      </View>
      )}

      <ShareModal
        visible={showShareModal}
        onClose={() => setShowShareModal(false)}
        url={shareUrl}
        title={shareTitle}
        message={shareMessage}
        language={i18n.language}
        analytics={shareTarget}
      />
    </View>
  );
};

const s = StyleSheet.create({
  // — Container modes —
  container: {
    backgroundColor: 'white',
    borderRadius: 24,
    marginTop: 32,
    overflow: 'hidden',
  },
  containerImmersive: {
    flex: 1,
    backgroundColor: '#FAFAF8',
    borderRadius: 0,
    overflow: 'hidden',
    ...(Platform.OS === 'web' && {
      display: 'flex' as any,
      flexDirection: 'column' as any,
      height: '100%' as any,
    }),
  },

  // — Header (Insight Timer: minimal, just connection dot + "About this Event") —
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 12,
  },
  headerImmersive: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(15, 20, 25, 0.06)',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  connectionDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
  },
  aboutEventLink: {
    color: '#3B82F6',
    fontSize: 13,
    fontWeight: '500',
  },

  // — Status —
  statusBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: 'rgba(15, 20, 25, 0.03)',
  },
  statusText: { flex: 1 },
  retryBtn: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 8,
    backgroundColor: 'rgba(237, 116, 61, 0.1)',
  },
  retryText: { color: accentTextOn('#E98245'), fontWeight: fontWeights.bold },

  // — Messages (Insight Timer style — avatar + inline text, no bubbles) —
  // The 400px cap lives in its OWN style rather than being un-set later.
  // `messageListImmersive` used to carry `maxHeight: undefined` to cancel it,
  // but an undefined value doesn't clear an earlier one when RN-web flattens a
  // style array — so the cap survived and the immersive panel rendered a 400px
  // message list inside a 604px column, wasting ~200px and clipping the last
  // message. Applying the cap only when it's wanted removes the whole class of
  // problem.
  messageList: {},
  messageListCapped: {
    maxHeight: 400,
    minHeight: 200,
  },
  messageListImmersive: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: 0,
      overflow: 'auto' as any,
    }),
  } as any,
  messageLink: {
    color: accentTextOn('#E98245'),
    textDecorationLine: 'underline',
  },
  messageListContent: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 80,
    gap: 8,
  },
  emptyIconWrap: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: 'rgba(237, 116, 61, 0.04)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  emptyText: {
    textAlign: 'center',
    fontSize: 13,
    fontWeight: '500',
  },
  emptyHint: {
    textAlign: 'center',
    fontSize: 13,
  },
  messageRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 16,
  },
  avatarContainer: {
    alignItems: 'center',
    flexShrink: 0,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    color: 'white',
    fontWeight: fontWeights.bold,
    fontSize: 14,
  },
  hostBadge: {
    marginTop: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: HOST_BADGE_FILL,
  },
  hostBadgeText: {
    color: 'white',
    fontSize: 12,
    fontWeight: fontWeights.bold,
    letterSpacing: 0.5,
  },
  messageContent: {
    flex: 1,
    paddingTop: 2,
  },
  messageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 3,
  },
  authorName: {
    fontWeight: fontWeights.bold,
    fontSize: 13,
    color: '#2B2730',
  },
  authorNameHost: {
    color: '#3B82F6',
  },
  messageTime: {
    fontSize: 13,
  },
  messageText: {
    lineHeight: 20,
    fontSize: 14,
  },

  // — Error —
  errorBanner: {
    paddingHorizontal: 20,
    paddingVertical: 8,
    backgroundColor: 'rgba(237, 116, 61, 0.06)',
  },
  errorText: { color: accentTextOn('#E98245'), textAlign: 'center' },

  // — Donation Panel —
  donationPanel: {
    paddingHorizontal: 20,
    paddingVertical: 18,
    borderTopWidth: 1,
    borderTopColor: 'rgba(15, 20, 25, 0.06)',
    backgroundColor: 'rgba(237, 116, 61, 0.025)',
  },
  donationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  donationTitle: {
    fontWeight: fontWeights.bold,
    letterSpacing: 2,
    fontSize: 12,
    color: accentTextOn(colors.solar),
  },
  donationGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  donationBtn: {
    flex: 1,
    minWidth: '40%' as any,
    height: 44,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: 'rgba(237, 116, 61, 0.25)',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'white',
    ...(Platform.OS === 'web' && {
      transition: 'all 0.15s ease',
    }),
  } as any,
  donationBtnText: {
    color: accentTextOn(colors.solar),
    fontSize: 14,
    fontWeight: fontWeights.bold,
    letterSpacing: 0,
    textTransform: 'none',
  },
  donationCustomBtn: {
    marginTop: 8,
    height: 38,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(15, 20, 25, 0.04)',
  },
  donationCustomText: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 0.5,
    color: colors.gray,
    textTransform: 'none',
  },

  // — Input area (ALWAYS visible — pinned to bottom) —
  inputArea: {
    borderTopWidth: 1,
    borderTopColor: 'rgba(15, 20, 25, 0.06)',
    ...(Platform.OS === 'web' && {
      marginTop: 'auto' as any,
      flexShrink: 0,
    }),
  } as any,
  inputBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingTop: 16,
    paddingBottom: 12,
    gap: 6,
  },
  textInput: {
    flex: 1,
    height: 38,
    borderRadius: 19,
    paddingHorizontal: 16,
    backgroundColor: 'rgba(15, 20, 25, 0.04)',
    fontSize: 13,
    fontFamily: fonts.text,
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none',
    }),
  } as any,
  sendBtn: {
    width: 38,
    height: 38,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' && {
      transition: 'opacity 0.15s ease',
      cursor: 'pointer',
    }),
  } as any,
  sendIconCircle: {
      width: 32,
      height: 32,
      borderRadius: 16,
      backgroundColor: '#2B2730',
      alignItems: 'center',
      justifyContent: 'center',
  },
  sendBtnDisabled: {
    opacity: 0.35,
  },

  // CORE-529 — guest composer replacement. Occupies the same slot as
  // `inputBar` so the panel doesn't jump when a viewer signs in mid-stream.
  signInBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingTop: 16,
    paddingBottom: 12,
    minHeight: 66,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  } as any,
  signInText: {
    color: accentTextOn(colors.solar),
    fontSize: 13,
    fontWeight: fontWeights.bold,
  },
  // CORE-1253 — кнопка под историей завершённой Live: в обсуждение анонса.
  continueButton: {
    marginTop: 12,
    marginHorizontal: 16,
  },

  // — Bottom bar: participant avatars + Donate —
  bottomBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingBottom: 24,
    paddingTop: 8,
  },
  participantStack: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  participantAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 2.5,
    borderColor: '#FAFAF8',
    alignItems: 'center',
    justifyContent: 'center',
  },
  participantAvatarText: {
    color: 'white',
    fontSize: 12,
    fontWeight: fontWeights.bold,
  },
  participantMore: {
    backgroundColor: 'rgba(15, 20, 25, 0.08)',
  },
  participantMoreText: {
    color: 'rgba(15, 20, 25, 0.5)',
    fontSize: 13,
    fontWeight: fontWeights.bold,
  },
  donateBtn: {
    paddingHorizontal: 26,
    paddingVertical: 12,
    borderRadius: 14,
    borderWidth: 0,
    backgroundColor: colors.solar,
    ...(Platform.OS === 'web' && {
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      boxShadow: '0 2px 12px rgba(237, 116, 61, 0.35)',
    }),
  } as any,
  // CORE-1733 — тёмная подпись на #D14E00 давала 3.6:1, белая — 4.4; на вебе
  // нажатое состояние — первый стоп CTA-градиента (#221A16 на нём 4.93).
  donateBtnActive: {
    backgroundColor: WEB ? '#F45122' : '#D14E00',
  },
  donateBtnText: {
    color: ON_ACCENT_TEXT,
    fontSize: 14,
    fontWeight: fontWeights.bold,
    letterSpacing: 0.3,
    textTransform: 'none',
  },

  // — Inline "About this Event" Panel (inside sidebar) —
  aboutPanel: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(15, 20, 25, 0.06)',
    backgroundColor: '#FAFAF8',
  },
  aboutCloseBtn: {
    position: 'absolute',
    top: 8,
    right: 12,
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: 'rgba(15, 20, 25, 0.05)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  aboutMasterAvatar: {
    width: 52,
    height: 52,
    borderRadius: 26,
    marginBottom: 14,
  },
  aboutTitle: {
    fontSize: 17,
    fontWeight: fontWeights.bold,
    color: '#2B2730',
    lineHeight: 22,
    marginBottom: 4,
    fontFamily: fonts.heading,
  },
  aboutMasterInfo: {
    fontSize: 12,
    color: 'rgba(15, 20, 25, 0.45)',
    fontWeight: '500',
    marginBottom: 14,
  },
  aboutDescription: {
    fontSize: 13,
    lineHeight: 20,
    color: 'rgba(15, 20, 25, 0.6)',
    marginBottom: 4,
  },
});
